/**
 * F 227 ITEM A — `fc_entity_history`. A collection, not an EntityKind.
 *
 * WHY IT IS NOT A KIND. `BaseEntity` is the shape a THING has. A history
 * record is not a thing you navigate to, own, or put in a tree — it is a
 * fact about a write that already happened. Making it a kind would put it
 * in every `childrenOf`, every kind-filtered list, and every tree walk in
 * the app, for records that are append-only and terminal. So this declares
 * a COLLECTION: a named, additive bucket with its own keyspace, readable
 * through its own helper, and invisible to every entity list that exists.
 *
 * WHY IT IS ADDITIVE. FCr's storage is ONE PocketBase collection keyed by
 * `(collection, key)` (PocketBaseAdapter, line 63), and the browser
 * equivalent is a `freshcards:` key prefix (RemoteState `SYNC_PREFIX`).
 * Declaring a new collection is therefore just a new name in an existing
 * keyspace — no PB schema change, no new column, no migration, and no
 * rewrite of an existing row. Rows that predate F 227 have no history and
 * that is correct: history starts at the first write after this dispatch.
 *
 * WHY A FIELD-LEVEL DELTA. A full-entity snapshot per write is the repo's
 * version control wearing a different hat, and it grows without bound on
 * any entity anyone edits often. The thing worth retrieving six months
 * later is "which field moved, from what, to what". That is what is
 * stored. A field that did not change is not in the record at all, so a
 * record's size is proportional to the edit, not to the entity.
 *
 * F 227 — read-only by design. Nothing here writes a history record that
 * was not produced by a real write path. There is no UI in this dispatch.
 */

import { RemoteState } from '../storage/RemoteState';

/** The collection name. Shared so the key builder has exactly one spelling. */
export const HISTORY_COLLECTION = 'fc_entity_history';

/** The FCr keyspace for a collection. Mirrors `freshcards:pages`. */
export const HISTORY_PREFIX = `freshcards:${HISTORY_COLLECTION}:`;

/** One field's movement. `null` means "was absent" or "became absent". */
export interface FieldDelta {
  before: unknown;
  after: unknown;
}

/** `changedFields` is a field-level delta — never a full entity. */
export type ChangedFields = Record<string, FieldDelta>;

export interface HistoryRecord {
  /** Record id. Unique; see `makeHistoryId`. */
  id: string;
  /** What changed. */
  entityId: string;
  /** ISO 8601. */
  timestamp: string;
  /** Field-level: only the fields that actually moved. */
  changedFields: ChangedFields;
  /** Who, when the session can say. */
  actor?: string;
  /** Optional note. */
  reason?: string;
}

export interface RecordHistoryOptions {
  /** Cap the returned list. Applied after the newest-first sort. */
  limit?: number;
  /** ISO 8601 lower bound, inclusive. */
  since?: string;
}

export interface AppendHistoryOptions {
  actor?: string;
  reason?: string;
  /** Overrides the stamp. Tests and replays pass an explicit time. */
  timestamp?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Diffing
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The scalar-ish fields worth a `before`/`after`. Deliberately not
 * "everything": these are the fields whose movement is a decision someone
 * might want to explain later.
 */
export const TRACKED_FIELDS = [
  'title',
  'name',
  'body',
  'content',
  'parentId',
  'order',
  'edge',
  'status',
  'kind',
  'deletedAt',
  'archived',
  'assignee',
  'dueDate',
  'priority',
] as const;

/**
 * Field-level delta between two versions of an entity.
 *
 * Returns `{}` when nothing tracked changed — and a create is NOT `{}`,
 * because every tracked field of a new entity genuinely went absent→present.
 */
export function diffEntity(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  fields: readonly string[] = TRACKED_FIELDS,
): ChangedFields {
  const out: ChangedFields = {};
  for (const f of fields) {
    const b = before?.[f];
    const a = after?.[f];
    if (b === a) continue;
    // `undefined` is normalised to null so a missing field and an explicit
    // null are the same delta. Otherwise "the key was added" and "the value
    // became null" would record as two different things.
    out[f] = { before: b === undefined ? null : b, after: a === undefined ? null : a };
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Write path
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Record ids sort by time, then by a counter, so two writes in the same
 * millisecond still have a stable order. The counter is deliberately
 * process-local: history is a read-back log, not a distributed sequence.
 */
let historySeq = 0;

function makeHistoryId(entityId: string, at: string): string {
  historySeq = (historySeq + 1) % 100000;
  return `${at}-${entityId}-${String(historySeq).padStart(5, '0')}`;
}

function keyFor(recordId: string): string {
  return `${HISTORY_PREFIX}${recordId}`;
}

/**
 * Append one history record. Returns the record as stored, or `null` if
 * there is genuinely nothing to record (an empty delta on an update).
 *
 * A no-op write — the same content saved again — appends nothing. That is
 * the point of a field-level delta: "the change log" should not fill up
 * with the timestamps of people opening a page.
 */
export function appendHistory(
  entityId: string,
  changedFields: ChangedFields,
  options: AppendHistoryOptions = {},
): HistoryRecord | null {
  if (!entityId) return null;
  if (!changedFields || Object.keys(changedFields).length === 0) return null;

  const at = options.timestamp ?? new Date().toISOString();
  const record: HistoryRecord = {
    id: makeHistoryId(entityId, at),
    entityId,
    timestamp: at,
    changedFields,
  };
  if (options.actor) record.actor = options.actor;
  if (options.reason) record.reason = options.reason;

  try {
    RemoteState.set(keyFor(record.id), JSON.stringify(record));
  } catch (error) {
    // History is a ledger. A failure to append must not take the WRITE
    // down with it — losing the edit is worse than losing the record of
    // the edit. The error is surfaced, not swallowed.
    console.error('[fc_entity_history] Failed to append record:', error);
    return null;
  }
  return record;
}

/**
 * The write path itself: record the delta for one save.
 * Used by every writer so no writer can forget.
 */
export function recordEntityChange(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  options: AppendHistoryOptions = {},
): HistoryRecord | null {
  const entityId = (after?.id as string) ?? (before?.id as string) ?? '';
  return appendHistory(entityId, diffEntity(before, after), options);
}

// ─────────────────────────────────────────────────────────────────────────────
// Read helper
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `historyOf(entityId, options?)` — newest first.
 *
 * `since` is ISO 8601 and INCLUSIVE. Comparison is on the timestamp string
 * because every stamp this module writes is UTC ISO, which sorts
 * lexicographically; a locale `Date` parse would only introduce a way for
 * the two to disagree.
 */
export function historyOf(
  entityId: string,
  options: RecordHistoryOptions = {},
): HistoryRecord[] {
  let records: HistoryRecord[] = [];
  try {
    for (const key of RemoteState.keys()) {
      if (!key.startsWith(HISTORY_PREFIX)) continue;
      const raw = RemoteState.get(key);
      if (!raw) continue;
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        continue; // a corrupt record must not hide the readable ones
      }
      const rec = parsed as HistoryRecord;
      if (rec && rec.entityId === entityId) records.push(rec);
    }
  } catch (error) {
    console.error('[fc_entity_history] Failed to read history:', error);
    return [];
  }

  records.sort((a, b) => (a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : a.id < b.id ? 1 : -1));

  if (options.since) records = records.filter((r) => r.timestamp >= options.since!);
  if (typeof options.limit === 'number' && options.limit >= 0) records = records.slice(0, options.limit);
  return records;
}

/** Every entity id with at least one record. Sorted. */
export function historyEntityIds(): string[] {
  const seen = new Set<string>();
  try {
    for (const key of RemoteState.keys()) {
      if (!key.startsWith(HISTORY_PREFIX)) continue;
      const raw = RemoteState.get(key);
      if (!raw) continue;
      try {
        const rec = JSON.parse(raw) as HistoryRecord;
        if (rec?.entityId) seen.add(rec.entityId);
      } catch {
        /* corrupt record: skip, same as historyOf */
      }
    }
  } catch {
    return [];
  }
  return [...seen].sort();
}
